// ACC-434: the user-navigable /tickets route renders this. It supplies the
// ticketing app with (a) Forecast's singleton Relay 13 environment via
// RelayProvider, and (b) a session identity derived from the Forecast viewer via
// ticketing's AuthProvider, so ProtectedRoute/RequireAdmin gate correctly.
// Full AuthContext replacement is ACC-436; this is the minimal identity bridge.
// ACC-438 reconciles ticketing's own AppNav with Forecast's shell.
import React from 'react';
import {graphql} from 'react-relay';
import {RelayProvider} from './forecast-app/ticketing/graphql/RelayProvider';
import {AuthProvider} from './forecast-app/ticketing/features/auth/context/AuthContext';
import TicketingApp from './forecast-app/ticketing/App';
// ABC Whyte @font-face + --fd-font-family (ACC-523: Tailwind removed).
import './forecast-app/ticketing/index.css';
import type {ticketingEmbed_Query$data} from './__generated__/ticketingEmbed_Query.graphql';

// Loaded by routerv2's ForecastQueryRenderer to gate login + supply identity.
export const TicketingEmbedQuery = graphql`
	query ticketingEmbed_Query {
		viewer {
			actualPersonId
			firstName
			lastName
			fullName
			email
			permissions
		}
	}
`;

export interface TicketingEmbedProps {
	viewer: NonNullable<ticketingEmbed_Query$data['viewer']>;
}

// Local mirror of ticketing's ForecastIdentity. ACC-502 relocated the ticketing
// source into web-front, but its TS 5.6 tree is excluded from web-front's TS 4.6
// fork-ts-checker pass, so this wrapper keeps a local identity type rather than
// importing ForecastIdentity across the seam. ACC-436: this is the identity
// bridge — the ticketing client derives its coarse role (agent/lead/admin) from
// the granular ticketPerms we forward here, rather than us pre-computing it.
interface TicketingIdentity {
	id: string;
	name: string;
	email: string;
	ticketPerms: string[];
}

/**
 * Renders the ticketing application root inside the Forecast shell. Mounted by
 * routerv2 under `<VersionReloadRoute path="/tickets">` (inside the existing
 * ForecastThemeProvider), gated by the `tickets_enabled` flag + TICKETS_VIEW.
 */
export const TicketingEmbed: React.FC<TicketingEmbedProps> = ({viewer}) => {
	const permissions = viewer.permissions ?? [];
	// ACC-436: hand Forecast's granular ticket permissions to the ticketing
	// client, which derives its coarse role (agent/lead/admin) from them. Only
	// TICKETS_* perms are relevant to ticketing.
	const ticketPerms = permissions.filter((p): p is string => typeof p === 'string' && p.startsWith('TICKETS_'));
	const identity: TicketingIdentity = {
		id: viewer.actualPersonId ?? '',
		name: viewer.fullName ?? [viewer.firstName, viewer.lastName].filter(Boolean).join(' '),
		email: viewer.email ?? '',
		ticketPerms,
	};
	return (
		<RelayProvider>
			<AuthProvider identity={identity}>
				{/* The Forecast shell (.app) is `height: 100vh; overflow: hidden; flex-column`
				    and delegates scrolling to each page (see ClientsPage's ScrollContainer).
				    Ticketing brings no scroll region of its own, so its content overflowed the
				    clipped shell and the bottom was unreachable. This host-owned wrapper becomes
				    the direct flex child of .app: it fills the height left by PrimaryNavigation
				    and scrolls internally. */}
				<div style={{flex: '1 1 auto', minHeight: 0, overflowY: 'auto'}}>
					{/* ACC-438: ticketing runs exclusively embedded — App hardcodes
					    EmbeddedProvider embedded={true} and drops its own AppNav/MobileDrawer;
					    Forecast's shell is the only chrome. */}
					<TicketingApp />
				</div>
			</AuthProvider>
		</RelayProvider>
	);
};

export default TicketingEmbed;
